import { createContext, useContext } from 'react';
import type { 
  CaseAccessRole, 
  CaseAccessDeniedReason, 
  CaseMemberPermissions,
  CaseMemberDocument,
} from '@/types';

/**
 * Case Access Context Value
 * 
 * This context provides case-level access control state to all child components
 * within a case workspace route. It uses React Context (not Outlet context) to
 * ensure proper re-render propagation when permissions hydrate.
 * 
 * CRITICAL: Provider value must be a NEW object when any field changes.
 * No mutation-in-place. This ensures React's reference equality check triggers re-renders.
 */
export interface CaseAccessContextValue {
  /** The current case ID */
  caseId: string;
  
  /** Whether permissions are still loading */
  loading: boolean;
  
  /** Whether the current user has access to this case */
  hasAccess: boolean;
  
  /** Reason for denial if !hasAccess and !loading */
  deniedReason: CaseAccessDeniedReason | null;
  
  /** User's role in this case */
  role: CaseAccessRole | null;
  
  /** Full membership document from Firestore */
  membership: CaseMemberDocument | null;
  
  /** Granular permissions object */
  permissions: CaseMemberPermissions | null;
  
  /** 
   * Version counter - increments each time permissions complete hydration.
   * Used for debugging/testing only (not for UI logic).
   */
  _version: number;
  
  /**
   * Phase 3.1: Access resolved flag
   * True when guard has made access decision (loading complete and either granted or denied).
   * This is distinct from permissionsResolved - access can be granted but permissions still loading.
   */
  accessResolved: boolean;
  
  /**
   * Phase 3.1: Permissions resolved flag
   * True when permissions object has been computed (permissions !== null).
   * For most cases this happens simultaneously with access grant, but can lag due to:
   * - Async permission derivation from role
   * - Membership document permissions field hydration
   */
  permissionsResolved: boolean;
}

/**
 * React Context for Case Access State
 * 
 * This replaces the Outlet context pattern which had re-render propagation issues.
 * Using standard React Context ensures child components re-render when permissions load.
 */
export const CaseAccessContext = createContext<CaseAccessContextValue | null>(null);

/**
 * Hook: useCaseAccessContext
 * 
 * Access case permissions and role information. Must be used within a CaseAccessProvider.
 * 
 * @throws Error if used outside of CaseAccessProvider
 * 
 * @example
 * ```tsx
 * const { permissions, loading } = useCaseAccessContext();
 * const canWrite = permissions?.canWriteJournal ?? false;
 * 
 * if (loading) return <Spinner />;
 * return <Button disabled={!canWrite}>New Entry</Button>;
 * ```
 */
export function useCaseAccessContext(): CaseAccessContextValue {
  const context = useContext(CaseAccessContext);
  
  if (context === null) {
    throw new Error(
      'useCaseAccessContext must be used within a CaseAccessProvider. ' +
      'This hook should only be called from components rendered within a case workspace route.'
    );
  }
  
  return context;
}

/**
 * Hook: useCasePermissions
 * 
 * Convenience hook that returns only permissions and loading state.
 * Useful when you don't need role/membership details.
 * 
 * **DEFENSIVE VERSION**: Unlike useCaseAccessContext(), this hook gracefully
 * handles missing provider context by returning null permissions. This allows
 * global layout components (TopHeader, PrimarySidebar) to conditionally render
 * case-scoped UI when context is available.
 * 
 * @returns {Object} - { permissions, loading }
 *   - permissions: CaseMemberPermissions | null (null when not in case context)
 *   - loading: boolean (false when not in case context)
 * 
 * @example
 * ```tsx
 * const { permissions, loading } = useCasePermissions();
 * 
 * // Outside case context: permissions=null, loading=false
 * // Inside case context while loading: permissions=null, loading=true
 * // Inside case context after load: permissions={...}, loading=false
 * 
 * const canEdit = permissions?.canWriteJournal ?? false;
 * ```
 */
export function useCasePermissions() {
  const context = useContext(CaseAccessContext);
  
  // Graceful fallback when not in CaseAccessProvider tree
  // (e.g., global layout components like TopHeader)
  if (context === null) {
    return { permissions: null, loading: false };
  }
  
  return { permissions: context.permissions, loading: context.loading };
}

/**
 * Hook: useHasCasePermission
 * 
 * Check a specific permission with proper three-state logic:
 * - loading=true: permission unknown (show loading state)
 * - loading=false + hasPermission=false: denied (show disabled/hidden)
 * - loading=false + hasPermission=true: allowed (enable action)
 * 
 * @param permissionKey - The permission to check (canWriteJournal, canWriteExhibits, etc.)
 * 
 * @example
 * ```tsx
 * const { hasPermission, loading } = useHasCasePermission('canWriteJournal');
 * 
 * if (loading) {
 *   return <Button disabled><Spinner /> New Entry</Button>;
 * }
 * 
 * if (!hasPermission) {
 *   return <Tooltip content="No permission"><Button disabled>New Entry</Button></Tooltip>;
 * }
 * 
 * return <Button onClick={handleCreate}>New Entry</Button>;
 * ```
 */
export function useHasCasePermission(
  permissionKey: keyof CaseMemberPermissions
): { hasPermission: boolean; loading: boolean } {
  const { permissions, loading } = useCaseAccessContext();
  
  // While loading, permission is unknown
  if (loading) {
    return { hasPermission: false, loading: true };
  }
  
  // After loading, check the specific permission
  const hasPermission = permissions?.[permissionKey] ?? false;
  return { hasPermission, loading: false };
}
